/**
 * Custo ESTIMADO de um Broadcast de Ligação (CU-17tm466nzee) — lógica pura, sem Angular.
 *
 * É só informativo: quem decide se a campanha sai é o backend (422 de saldo + modal T.10).
 * Nada daqui pode bloquear o envio nem ser tratado como valor cobrado.
 *
 * ## O que o backend cobra (conferido no código, 14/10/2026)
 *
 * - Unidade: bloco de 30 s do ÁUDIO inteiro, `Call4uAudioService::calculateCredits()` =
 *   `ceil(segundos / config('call4u.seconds_per_credit', 30))`. O preço da carteira vem por
 *   bloco (`WalletProductPrice.unit === 'block_30s'`).
 * - Só a ligação ATENDIDA é cobrada: `Call4uCallbackService::settleWalletHold()` captura o
 *   hold no `ANSWER` e solta a reserva em BUSY/NOANSWER/CONGESTION/CHANUNAVAIL (o balde de
 *   créditos segue a mesma regra).
 * - Retentativa só nasce para quem NÃO atendeu (`scheduleRetry` só para DECLINED_STATUSES).
 *   Logo cada contato é cobrado NO MÁXIMO UMA VEZ, qualquer que seja o nº de tentativas.
 * - O aviso de saldo do backend (`MassSendBalanceCheck::call`) usa `leads × blocos`, "a 1ª
 *   tentativa de cada lead", com `calculateCredits(max(1, segundos))`.
 *
 * Portanto a faixa real é: mínimo 0 (ninguém atende) e máximo `leads × blocos × preço`
 * (todos atendem, em qualquer tentativa). As tentativas multiplicam DISCAGENS, não custo.
 * O máximo coincide com o número que o backend compara com o saldo.
 */

/** Segundos por bloco cobrado. Espelha o default de `call4u.seconds_per_credit`. */
export const CALL_SECONDS_PER_BLOCK = 30;

/** 1 centavo em micros (1 BRL = 1.000.000). */
const CENT_MICROS = 10_000;

/**
 * Blocos de 30 s de um áudio: cada 30 s ou fração = 1 bloco, mínimo 1 (o mesmo
 * `max(1, segundos)` do aviso de saldo do backend).
 */
export function callBlocks(seconds: number): number {
  const safe = Number.isFinite(seconds) ? seconds : 0;
  return Math.max(1, Math.ceil(safe / CALL_SECONDS_PER_BLOCK));
}

export interface CallCostInput {
  /** Contatos da lista. */
  leads: number;
  /** Tentativas máximas (1-5). Só entra em `maxDials`: não multiplica o custo. */
  attempts: number;
  /** Duração do áudio em segundos. */
  audioSeconds: number;
  /** Preço por bloco de 30 s, em micros (ou 1 para contar em créditos — ver `callCreditsRange`). */
  unitPriceMicros: number;
}

export interface CallCostRange {
  blocks: number;
  /** Custo de UMA ligação atendida. */
  perCallMicros: number;
  /** Ninguém atende: nada é cobrado. Sempre 0 — está aqui para a faixa ficar explícita. */
  minMicros: number;
  /** Todos atendem (em qualquer tentativa): `leads × perCall`. É o teto do que pode ser cobrado. */
  maxMicros: number;
  /** Discagens no pior caso (ninguém atende e todas as tentativas saem): `leads × attempts`. */
  maxDials: number;
}

/**
 * Faixa de custo, ou `null` quando não dá para estimar com honestidade — e aí a tela
 * esconde o cartão em vez de mostrar `R$ 0,00`:
 *  - preço ausente, zero, negativo ou não numérico (produto sem preço é "não sai", não grátis);
 *  - lista sem contagem ou vazia;
 *  - áudio sem duração medida (0/ausente): o despacho cobraria pela duração real, que ainda
 *    não se conhece, e estimar 1 bloco poderia subestimar.
 */
export function callCostRange(input: CallCostInput | null | undefined): CallCostRange | null {
  if (!input) {
    return null;
  }

  const { leads, attempts, audioSeconds, unitPriceMicros } = input;

  if (!isPositive(unitPriceMicros) || !isPositive(leads) || !isPositive(audioSeconds)) {
    return null;
  }

  const safeLeads = Math.floor(leads);
  const safeAttempts = Number.isFinite(attempts) ? Math.min(5, Math.max(1, Math.floor(attempts))) : 1;
  const blocks = callBlocks(audioSeconds);
  const perCallMicros = blocks * unitPriceMicros;
  const maxMicros = safeLeads * perCallMicros;

  return {
    blocks,
    perCallMicros,
    minMicros: 0,
    maxMicros,
    maxDials: safeLeads * safeAttempts,
  };
}

/**
 * Mesma faixa contada em créditos de ligação (conta no pacote): 1 crédito É 1 bloco de 30 s
 * (`WalletCallCharge`, "a unidade é o bloco de 30 s"), então é a conta com preço unitário 1.
 */
export function callCreditsRange(input: Omit<CallCostInput, 'unitPriceMicros'>): CallCostRange | null {
  return callCostRange({ ...input, unitPriceMicros: 1 });
}

/**
 * Arredonda micros PARA CIMA até o centavo, para exibir com 2 casas sem nunca mostrar menos
 * do que a conta dá (ex.: 1.234.001 micros vira R$ 1,24, não R$ 1,23).
 */
export function ceilToCentMicros(micros: number): number {
  return Math.ceil(micros / CENT_MICROS) * CENT_MICROS;
}

function isPositive(value: number): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
